// src/config/checkinItems.js
//
// Single source of truth for the Student CHECK-IN system. This mirrors
// config/roles.js -> CHECKOUT_SECTIONS for the checkout side, but each item
// carries more per-item config because check-in items vary by audience and
// required-ness.
//
// Each item has:
//   id          stable slug, used as the key everywhere (Firestore, records)
//   label       human label shown on the checklist
//   description short helper text shown under the label
//   department  default "owning" department, shown for contact purposes
//   audience    array of audience tags — who this item pertains to.
//                 Valid tags: 'all' | 'day' | 'boarding' | 'international'
//                 'all' is exclusive (see normalizeAudience).
//   required    whether the item is required for the student to be fully
//                 "checked in"
//
// IMPORTANT: the values below are DEFAULTS. Every field except `id`/`label`
// can be overridden per-item from the Super Admin panel, which persists to
// /config/checkinPointPeople. Staff assignments ("who can check this off")
// live ONLY in that Firestore doc, never here. getResolvedCheckinItems()
// merges the static defaults with the Firestore overrides.
//
// The audience defaults below are best-guess starting points — confirm/adjust
// them from the Super Admin panel. (See the field-name notes on
// isBoardingStudent / isInternationalStudent for the /students schema reads.)

// Keep this aligned with checkoutHelpers.DEFAULT_SCHOOL_YEAR.
export const DEFAULT_CHECKIN_SCHOOL_YEAR = '2025-2026';

// Firestore doc holding all editable check-in config.
export const CHECKIN_CONFIG_DOC = 'checkinPointPeople';

// Eternal owner fallback (matches the checkout/superadmin convention).
const OWNER_UID = 'IOj8t19zqBRJynu45HRKqf8J0MB2';

// The four audience tags a check-in item can target.
export const CHECKIN_AUDIENCES = [
  { id: 'all', label: 'All students' },
  { id: 'day', label: 'Day students' },
  { id: 'boarding', label: 'Boarding students' },
  { id: 'international', label: 'International students' },
];

const VALID_AUDIENCE_TAGS = new Set(CHECKIN_AUDIENCES.map((a) => a.id));

// ─── The checklist ──────────────────────────────────────────────────────────
// Order here is the display order in the panel and on the checklist.

export const CHECKIN_ITEMS = [
  {
    id: 'finance',
    label: 'Finance Cleared',
    description: 'Tuition and fees cleared by the Finance office.',
    department: 'Finance',
    audience: ['all'],
    required: true,
  },
  {
    id: 'handbook',
    label: 'Student/Parent Handbook',
    description: 'Handbook reviewed and acknowledged.',
    department: 'Student Life',
    audience: ['all'],
    required: true,
  },
  {
    id: 'campus_entry',
    label: 'Campus Entry Permission Form',
    description: 'Permission for the student to enter and be on campus.',
    department: 'Security & Operations',
    audience: ['all'],
    required: true,
  },
  {
    id: 'health_screening',
    label: 'Health Screening Form',
    description: 'Completed health screening on file with the Health Center.',
    department: 'Health Center',
    audience: ['all'],
    required: true,
  },
  {
    id: 'insurance',
    label: 'Insurance Form',
    description: 'Medical insurance details submitted.',
    department: 'Health Center',
    audience: ['all'],
    required: true,
  },
  {
    id: 'photo_policy',
    label: 'Photo Policy Form',
    description: 'Photo / media consent recorded.',
    department: 'Communications',
    audience: ['all'],
    required: true,
  },
  {
    id: 'transportation',
    label: 'Student Transportation Form',
    description: 'General transportation arrangements on file.',
    department: 'Operations',
    audience: ['all'],
    required: true,
  },
  {
    id: 'grandparent_info',
    label: 'Grandparent Information Form',
    description: 'Optional grandparent contact details for the family record.',
    department: 'Advancement',
    audience: ['all'],
    required: false,
  },
  {
    id: 'contact_verification',
    label: 'Contact Information Verification Form',
    description: 'Family contact details verified and current.',
    department: 'Registrar',
    audience: ['all'],
    required: true,
  },
  {
    id: 'communication_agreement',
    label: 'Communication Expectations Agreement',
    description: 'Agreement on school–home communication expectations.',
    department: 'Student Life',
    audience: ['all'],
    required: true,
  },
  {
    id: 'day_bus_service',
    label: 'Day Student Bus Service Form',
    description: 'Bus service election for day students who ride.',
    department: 'Transportation',
    audience: ['day'],
    required: false,
  },
  {
    id: 'boarding_permission',
    label: 'Boarding Student Permission Form',
    description: 'Boarding-specific permissions and acknowledgements.',
    department: 'Residential Life',
    audience: ['boarding'],
    required: true,
  },
  {
    id: 'cake_order',
    label: 'Cake Order Form',
    description: 'Optional birthday cake ordering preference.',
    department: 'Residential Life',
    audience: ['all'],
    required: false,
  },
  {
    id: 'laundry',
    label: 'Laundry Form',
    description: 'Laundry service election (boarding).',
    department: 'Residential Life',
    audience: ['boarding'],
    required: false,
  },
  {
    id: 'academics_registration',
    label: 'Academics and Course Registration',
    description: 'Course registration confirmed by the academic office.',
    department: 'Academics',
    audience: ['all'],
    required: true,
  },
  {
    id: 'immigration_visa',
    label: 'Immigration/Visa Clearance',
    description: 'Visa / immigration documentation cleared.',
    department: 'International Office',
    audience: ['international'],
    required: true,
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────

export function getCheckinItemConfig(id) {
  return CHECKIN_ITEMS.find((i) => i.id === id) || null;
}

// Normalize an audience array to a clean set of valid tags.
//   - Drops unknown tags.
//   - Empty -> ['all'].
//   - 'all' is exclusive: if present, returns just ['all'].
export function normalizeAudience(audience) {
  const cleaned = (Array.isArray(audience) ? audience : []).filter((a) =>
    VALID_AUDIENCE_TAGS.has(a),
  );
  if (cleaned.length === 0) return ['all'];
  if (cleaned.includes('all')) return ['all'];
  return Array.from(new Set(cleaned));
}

// Merge static CHECKIN_ITEMS defaults with the Firestore config doc
// (/config/checkinPointPeople). Returns an ordered array of fully-resolved
// items, each with: id, label, description, department, audience, required,
// signers. Order follows CHECKIN_ITEMS.
export function getResolvedCheckinItems(configDoc) {
  const overrides =
    configDoc && configDoc.items && typeof configDoc.items === 'object'
      ? configDoc.items
      : {};
  return CHECKIN_ITEMS.map((item) => {
    const o = overrides[item.id] || {};
    return {
      id: item.id,
      label: item.label,
      description: item.description || '',
      department:
        typeof o.department === 'string' ? o.department : item.department,
      audience: normalizeAudience(o.audience || item.audience),
      required: typeof o.required === 'boolean' ? o.required : !!item.required,
      signers: Array.isArray(o.signers) ? o.signers : [],
    };
  });
}

// ─── Student attribute reads ────────────────────────────────────────────────
//
// These decide whether an item applies to a student. Field names are matched
// to the actual /students schema (confirmed via Firestore console):
//   - residency lives in the `cohort` field ("Boarding" / "Day").
//   - there is currently NO international flag on student docs — see
//     isInternationalStudent below.
// The reads stay tolerant of a few alternate field names in case the schema
// shifts; keep these in sync with functions/checkin.js.

export function isBoardingStudent(student) {
  if (!student) return false;
  if (student.boarding === true) return true;
  // At KA, residency lives in the `cohort` field ("Boarding" / "Day").
  const r = String(
    student.cohort || student.residency || student.dayBoarding || student.boardingStatus || '',
  ).toLowerCase();
  return r.includes('board');
}

export function isInternationalStudent(student) {
  if (!student) return false;
  if (student.isInternational === true || student.international === true) {
    return true;
  }
  const v = String(student.studentType || student.nationality || '').toLowerCase();
  // NOTE: /students currently has NO international indicator, so this returns
  // false for everyone today. Items with audience ['international'] (e.g.
  // Immigration/Visa Clearance) therefore won't appear on any record until an
  // international field exists OR that item's audience is switched to 'all' in
  // the Super Admin panel. Update this read once the field is decided.
  return v.includes('international');
}

// Does a check-in item apply to a given student?
//   - audience includes 'all'           -> always
//   - audience includes 'day'           -> if the student is a day student
//   - audience includes 'boarding'      -> if the student is a boarder
//   - audience includes 'international'  -> if the student is international
export function itemAppliesToStudent(item, student) {
  const audience = normalizeAudience(item?.audience);
  if (audience.includes('all')) return true;

  const isBoarding = isBoardingStudent(student);
  const isDay = !isBoarding; // every student is one or the other
  const isIntl = isInternationalStudent(student);

  if (audience.includes('day') && isDay) return true;
  if (audience.includes('boarding') && isBoarding) return true;
  if (audience.includes('international') && isIntl) return true;
  return false;
}

// Given a resolved-items list and a student, return only the items that apply.
export function getApplicableItems(resolvedItems, student) {
  return (resolvedItems || []).filter((it) => itemAppliesToStudent(it, student));
}

// ─── Permission check ─────────────────────────────────────────────────────
//
// Can a given user check off a particular item? Superadmin/owner always can;
// otherwise the user's uid must appear in that item's `signers` list.
// `item` should be a resolved item (i.e. include `signers`).
export function canCheckInItem(user, item) {
  if (!user) return false;
  if (user.isSuperAdmin) return true;
  if (user.uid === OWNER_UID) return true;
  const signers = Array.isArray(item?.signers) ? item.signers : [];
  return signers.includes(user.uid);
}

// Does this user have check-in signing rights on ANY item? Used to decide
// whether to show the "My Pending" tab and whether they can create records.
export function hasAnyCheckinPermission(user, resolvedItems) {
  if (!user) return false;
  if (user.isSuperAdmin || user.uid === OWNER_UID) return true;
  return (resolvedItems || []).some((it) =>
    (it.signers || []).includes(user.uid),
  );
}

// The set of item ids a user can sign (excluding the global superadmin grant,
// which is handled separately by canCheckInItem). Useful for "My Pending".
export function getMyCheckinItemIds(user, resolvedItems) {
  if (!user) return [];
  return (resolvedItems || [])
    .filter((it) => (it.signers || []).includes(user.uid))
    .map((it) => it.id);
}